import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { Logo } from '@/components/Logo'

interface Plan {
  id: string
  name: string
  slug: string
  description: string
  price_monthly: number
  price_yearly: number
  links_per_month: number
  max_custom_domains: number
  api_requests_per_day: number
  rate_limit_per_minute: number
  analytics_retention_days: number
  features: string[]
  is_active: boolean
}

const featuresByPlan: Record<string, { text: string; included: boolean }[]> = {
  free: [
    { text: '50 links per month', included: true },
    { text: '100 API requests/day', included: true },
    { text: '7-day analytics', included: true },
    { text: 'Custom domains', included: false },
    { text: 'Webhooks', included: false },
    { text: 'White label', included: false },
  ],
  starter: [
    { text: '500 links per month', included: true },
    { text: '5,000 API requests/day', included: true },
    { text: '30-day analytics', included: true },
    { text: '1 custom domain', included: true },
    { text: 'Webhooks', included: false },
    { text: 'White label', included: false },
  ],
  pro: [
    { text: '5,000 links per month', included: true },
    { text: '50,000 API requests/day', included: true },
    { text: '1-year analytics', included: true },
    { text: '5 custom domains', included: true },
    { text: 'Webhooks', included: true },
    { text: 'White label', included: false },
  ],
  business: [
    { text: '25,000 links per month', included: true },
    { text: '500,000 API requests/day', included: true },
    { text: 'Unlimited analytics', included: true },
    { text: '20 custom domains', included: true },
    { text: 'Webhooks', included: true },
    { text: 'White label', included: true },
  ],
}

const comparisonRows = [
  { feature: 'Links per month', values: ['50', '500', '5,000', '25,000'] },
  { feature: 'Custom domains', values: ['0', '1', '5', '20'] },
  { feature: 'API requests/day', values: ['100', '5,000', '50,000', '500,000'] },
  { feature: 'Rate limit/minute', values: ['10', '60', '300', '1,000'] },
  { feature: 'Analytics retention', values: ['7 days', '30 days', '1 year', 'Forever'] },
  { feature: 'API access', values: [false, true, true, true] },
  { feature: 'Webhooks', values: [false, false, true, true] },
  { feature: 'White-label', values: [false, false, false, true] },
  { feature: 'Priority support', values: [false, false, false, true] },
]

const faqs = [
  {
    q: 'Can I upgrade or downgrade anytime?',
    a: "Yes! You can change your plan at any time. When upgrading, you'll be charged the prorated difference. When downgrading, the change takes effect at the end of your billing cycle.",
  },
  {
    q: 'What happens if I exceed my link limit?',
    a: "You'll receive a notification when you're approaching your limit. Once reached, you won't be able to create new links until the next billing cycle or until you upgrade.",
  },
  {
    q: 'Do you offer refunds?',
    a: "Yes, we offer a 14-day money-back guarantee on all paid plans. If you're not satisfied, contact us for a full refund.",
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe.',
  },
  {
    q: 'Is there an Enterprise plan?',
    a: 'Yes! For higher volume needs, custom domains, or specific requirements, contact our sales team for a custom Enterprise plan.',
  },
]

export function PricingPage() {
  const [isYearly, setIsYearly] = useState(false)
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const { user } = useAuthStore()

  useEffect(() => {
    fetch('/api/billing/plans')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load plans')
        return res.json()
      })
      .then(data => {
        setPlans(data)
        setLoading(false)
      })
      .catch(() => {
        setError(true)
        setLoading(false)
      })
  }, [])

  const getPrice = (plan: Plan) => {
    const cents = isYearly ? plan.price_yearly : plan.price_monthly
    return cents / 100
  }

  const getCta = (plan: Plan) => {
    if (plan.slug === 'free') return { label: 'Get Started', to: '/signup' }
    if (plan.slug === 'business') return { label: 'Contact Sales', to: '/signup' }
    return { label: 'Start Free Trial', to: user ? undefined : '/signup' }
  }

  const handlePaidCta = async (plan: Plan) => {
    if (!user) return // Link-based navigation handles this
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planId: plan.id,
          interval: isYearly ? 'yearly' : 'monthly',
        }),
      })
      const data = await res.json()
      if (data.url) {
        // eslint-disable-next-line react-hooks/immutability
        window.location.href = data.url
      }
    } catch {
      // Fallback to signup
      // eslint-disable-next-line react-hooks/immutability
      window.location.href = '/signup'
    }
  }

  return (
    <div className="min-h-screen bg-[#f6f6f8] dark:bg-[#111621] text-[#0e121b] dark:text-white">
      {/* Floating NavBar */}
      <div className="fixed top-6 left-0 right-0 z-50 flex justify-center px-4">
        <header className="flex items-center justify-between w-full max-w-[960px] bg-white/80 dark:bg-[#1c2230]/80 backdrop-blur-md border border-gray-200 dark:border-gray-800 rounded-full px-6 py-2.5 shadow-sm">
          <Link to="/">
            <Logo size="md" variant="auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link className="text-sm font-medium hover:text-[#1754cf] transition-colors" to="/#features">Features</Link>
            <Link className="text-sm font-medium text-[#1754cf] transition-colors" to="/pricing">Pricing</Link>
            <Link className="text-sm font-medium hover:text-[#1754cf] transition-colors" to="/login">Login</Link>
          </nav>
          <Link
            to="/signup"
            className="bg-[#1754cf] text-white text-sm font-bold px-5 py-2 rounded-full hover:opacity-90 transition-opacity"
          >
            Get Started
          </Link>
        </header>
      </div>

      <main className="pt-32">
        {/* Hero */}
        <section className="max-w-[960px] mx-auto px-6 py-16 text-center">
          <h1 className="text-4xl md:text-5xl font-black tracking-[-0.05em] leading-[1.1] mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-lg max-w-[600px] mx-auto mb-10">
            Choose the plan that fits your needs. Upgrade or downgrade anytime.
          </p>

          {/* Monthly/Yearly Toggle */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${!isYearly
                ? 'bg-[#1754cf] text-white'
                : 'bg-white dark:bg-[#1c2230] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-[#1754cf]'
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${isYearly
                ? 'bg-[#1754cf] text-white'
                : 'bg-white dark:bg-[#1c2230] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-[#1754cf]'
                }`}
            >
              Yearly
            </button>
            <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              Save 17%
            </span>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="max-w-[960px] mx-auto px-6 pb-20">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-[#1754cf] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <span className="material-symbols-outlined text-4xl text-gray-400">cloud_off</span>
              <p className="text-gray-500 dark:text-gray-400 text-center">Unable to load pricing plans. Please try again later.</p>
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-2 bg-[#1754cf] text-white text-sm font-semibold rounded-full hover:opacity-90 transition-opacity"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map(plan => {
                const cta = getCta(plan)
                const features = featuresByPlan[plan.slug] || []
                const isPro = plan.slug === 'pro'

                return (
                  <div
                    key={plan.id}
                    className={`relative p-6 rounded-2xl bg-white dark:bg-[#1c2230] border transition-colors ${isPro
                      ? 'border-[#1754cf] shadow-lg shadow-[#1754cf]/10'
                      : 'border-gray-200 dark:border-gray-800 hover:border-[#1754cf]'
                      }`}
                  >
                    {isPro && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1754cf] text-white text-xs font-bold px-4 py-1 rounded-full">
                        Most Popular
                      </div>
                    )}

                    <div className="mb-4">
                      <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {plan.slug === 'free' && 'Perfect for personal projects'}
                        {plan.slug === 'starter' && 'For growing businesses'}
                        {plan.slug === 'pro' && 'For power users & teams'}
                        {plan.slug === 'business' && 'For agencies & enterprises'}
                      </p>
                    </div>

                    <div className="mb-6">
                      <span className="text-4xl font-black">${getPrice(plan)}</span>
                      <span className="text-gray-500 dark:text-gray-400 text-sm">
                        /{isYearly ? 'year' : 'month'}
                      </span>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <span
                            className={`material-symbols-outlined text-[18px] ${f.included ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'
                              }`}
                          >
                            {f.included ? 'check_circle' : 'cancel'}
                          </span>
                          <span className={f.included ? '' : 'text-gray-400 dark:text-gray-600'}>
                            {f.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {cta.to ? (
                      <Link
                        to={cta.to}
                        className={`block w-full text-center py-3 rounded-xl font-bold text-sm transition-all ${isPro
                          ? 'bg-[#1754cf] text-white hover:opacity-90'
                          : 'border border-gray-200 dark:border-gray-700 hover:border-[#1754cf] hover:text-[#1754cf]'
                          }`}
                      >
                        {cta.label}
                      </Link>
                    ) : (
                      <button
                        onClick={() => handlePaidCta(plan)}
                        className={`block w-full text-center py-3 rounded-xl font-bold text-sm transition-all ${isPro
                          ? 'bg-[#1754cf] text-white hover:opacity-90'
                          : 'border border-gray-200 dark:border-gray-700 hover:border-[#1754cf] hover:text-[#1754cf]'
                          }`}
                      >
                        {cta.label}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </section>

        {/* Feature Comparison Table */}
        <section className="max-w-[960px] mx-auto px-6 py-20">
          <h2 className="text-3xl font-bold tracking-tight text-center mb-10">Compare All Features</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="text-left py-4 pr-4 font-semibold text-gray-500 dark:text-gray-400">Feature</th>
                  <th className="py-4 px-4 font-semibold">Free</th>
                  <th className="py-4 px-4 font-semibold">Starter</th>
                  <th className="py-4 px-4 font-semibold text-[#1754cf]">Pro</th>
                  <th className="py-4 px-4 font-semibold">Business</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-800/50">
                    <td className="text-left py-4 pr-4 font-medium">{row.feature}</td>
                    {row.values.map((val, j) => (
                      <td key={j} className="py-4 px-4 text-center">
                        {typeof val === 'boolean' ? (
                          <span
                            className={`material-symbols-outlined text-[18px] ${val ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'
                              }`}
                          >
                            {val ? 'check_circle' : 'cancel'}
                          </span>
                        ) : (
                          <span>{val}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="max-w-[700px] mx-auto px-6 py-20">
          <h2 className="text-3xl font-bold tracking-tight text-center mb-10">Frequently Asked Questions</h2>
          <div className="space-y-0">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-gray-200 dark:border-gray-800">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between py-5 text-left font-semibold hover:text-[#1754cf] transition-colors"
                >
                  {faq.q}
                  <span className={`material-symbols-outlined text-[20px] transition-transform ${openFaq === i ? 'rotate-180' : ''}`}>
                    expand_more
                  </span>
                </button>
                {openFaq === i && (
                  <p className="pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                    {faq.a}
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-[960px] mx-auto px-6 py-20 text-center">
          <h2 className="text-3xl font-bold tracking-tight mb-4">Ready to get started?</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">
            Join thousands of teams using URLzippy to power their links.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 bg-[#1754cf] text-white font-bold px-8 py-4 rounded-xl hover:opacity-90 transition-opacity"
          >
            Start for Free
            <span className="material-symbols-outlined">arrow_forward</span>
          </Link>
        </section>

        {/* Footer */}
        <footer className="max-w-[960px] mx-auto px-6 py-12 border-t border-gray-200 dark:border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Logo size="sm" variant="auto" />
            <p className="text-sm text-gray-400">&copy; {new Date().getFullYear()} URLzippy. All rights reserved.</p>
          </div>
        </footer>
      </main>
    </div>
  )
}
